import { useEffect, useState } from 'react';
import {
  WarehouseMatchList,
  Match,
  WarehouseData,
  Warehouse,
} from '@views/WarehouseDetails/WarehouseDetails';
import moment from 'moment';
import {
  BillingCycle,
  Invoice,
  InvoiceAdditionalService,
  ServiceRates,
  VAT_RATE,
} from './invoiceTypes';
import { useAuthHttp } from '../../hooks';
import { APPROVE_INVOICE, MATCH_LIST, WAREHOUSE_GET } from '../../constants';
import { InvoiceTable } from './InvoiceTable';
import { InvoiceSummary } from './InvoiceSummary';
import { InvoicePaymentDetails } from './InvoicePaymentDetails';
import { getInvoiceCost, getInvoiceVatCost, getVatCost } from './invoiceHelper';
import { PageHeader } from '../../tailwindComponents/PageHeader';

export const additionalServicesOptions = [
  {
    label: 'Case Picking and Despatch',
    value: 'casePickingAndDespatchPrice',
  },
  {
    label: 'Container Handling',
    value: 'containerHandlingPrice',
  },
  {
    label: 'Demurrage',
    value: 'demurragePricePerDay',
  },
  {
    label: 'Item Picking and Despatch',
    value: 'itemPickingAndDespatchPrice',
  },
  {
    label: 'Pallet Picking and Despatch',
    value: 'palletPickingAndDespatchPrice',
  },
  {
    label: 'Pallet Receipt and Put Away',
    value: 'palletReceiptAndPutAwayPrice',
  },
  {
    label: 'Storage',
    value: 'storagePricePerWeek',
  },
  {
    label: 'Pallet Returns',
    value: 'palletReturnsPrice',
  },
  {
    label: 'Case Returns',
    value: 'caseReturnsPrice',
  },
  {
    label: 'Item Returns',
    value: 'itemReturnsPrice',
  },
];

export function LSPInvoice({
  billingCycle,
  invoice,
}: {
  billingCycle: BillingCycle;
  invoice: Invoice;
}) {
  const authHttp = useAuthHttp();
  const [hasConfirmed, setHasConfirmed] = useState(!!invoice.approvedAt);
  const [invoiceLines, setInvoiceLines] = useState(invoice.lines);
  const [invoiceNotes, setInvoiceNotes] = useState(invoice.notes);
  const [hasPaid, setHasPaid] = useState(false);
  const [isConfirming, setIsConfirming] = useState(false);
  const [auxiliaryServicesPrice, setAuxiliaryServicesPrice] = useState(0);
  const [manHours, setManHours] = useState(0);
  const [palletsProvisionedQuantity, setPalletsProvisionedQuantity] =
    useState(0);
  const [palletsProvisionedCost, setPalletsProvisionedCost] = useState(0);
  const [palletsProvisionedVat, setPalletsProvisionedVat] = useState(0);
  const [surchargesAppliedQuantity, setSurchargesAppliedQuantity] = useState(0);
  const [surchargesAppliedCost, setSurchargesAppliedCost] = useState(0);
  const [surchargesAppliedVat, setSurchargesAppliedVat] = useState(0);
  const [otherAmount, setOtherAmount] = useState(0);
  const [otherAmountVat, setOtherAmountVat] = useState(0);
  const [additionalServices, setAdditionalServices] = useState<
    InvoiceAdditionalService[]
  >([]);
  const [match, setMatch] = useState<Match>();
  const [warehouse, setWarehouse] = useState<Warehouse>();
  const [dueDate, setDueDate] = useState(invoice?.dueDate);

  let subTotalAmount = getInvoiceCost(invoice);
  let subTotalVatAmount = getInvoiceVatCost(invoice);

  subTotalAmount += manHours * auxiliaryServicesPrice;
  subTotalVatAmount += (manHours * auxiliaryServicesPrice * VAT_RATE) / 100;

  subTotalAmount += palletsProvisionedCost;
  subTotalVatAmount += palletsProvisionedVat;

  subTotalAmount += surchargesAppliedCost;
  subTotalVatAmount += surchargesAppliedVat;

  subTotalAmount += otherAmount;
  subTotalVatAmount += otherAmountVat;

  additionalServices.forEach((service) => {
    const vatAmout = (service.amount * VAT_RATE) / 100;
    subTotalAmount += parseFloat(service.amount.toFixed(2));
    subTotalVatAmount += parseFloat(vatAmout.toFixed(2));
  });

  const fetchWarehouseMatch = async () => {
    try {
      const { data } = await authHttp.post<WarehouseMatchList>(
        MATCH_LIST,
        {
          warehouseId: billingCycle.warehouseId,
          matchId: billingCycle.warehouseMatchId,
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (data.matches.length > 0) {
        setAuxiliaryServicesPrice(
          data.matches[0].match.serviceRates.auxiliaryServicePricePerManHour
            ?.acceptedValue
        );
        setMatch(data.matches[0].match);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    }
  };

  const fetchWarehouse = async () => {
    try {
      const { data } = await authHttp.get<WarehouseData>(WAREHOUSE_GET, {
        params: {
          warehouseId: billingCycle.warehouseId,
        },
      });

      if (data.warehouse) {
        setWarehouse(data.warehouse);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleConfirmInvoice = async () => {
    setIsConfirming(true);
    const otherDescription = (
      document.getElementById('otherDescription') as HTMLInputElement
    ).value;

    try {
      const { data } = await authHttp.post<{ billingCycle: BillingCycle }>(
        APPROVE_INVOICE,
        {
          billingCycle: {
            billingCycleId: billingCycle.id,
            approvedAt: moment(),
            notes: (document.getElementById('comments') as HTMLInputElement)
              .value,
            auxiliaryServiceManHours:
              (document.getElementById('manHours') as HTMLInputElement)
                ?.value || null,
            palletsProvisioned: palletsProvisionedQuantity,
            surchargesApplied: surchargesAppliedQuantity,
            additionalServices:
              otherAmount > 0 && otherDescription
                ? [
                    ...additionalServices,
                    {
                      id: crypto.randomUUID(),
                      lineNumber:
                        invoice.lines.length + additionalServices.length + 2,
                      description: otherDescription,
                      amount: otherAmount,
                    },
                  ]
                : additionalServices,
          },
        },
        { headers: { 'Content-Type': 'application/json' } }
      );

      if (data.billingCycle?.lspToFloxInvoice) {
        setHasConfirmed(true);
        setInvoiceLines(data.billingCycle.lspToFloxInvoice.lines);
        setInvoiceNotes(data.billingCycle.lspToFloxInvoice.notes);
        setDueDate(data.billingCycle.lspToFloxInvoice.dueDate);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } finally {
      setIsConfirming(false);
    }
  };

  const handleAdditionalServiceDescriptionChange = (
    value: string,
    id: string
  ) => {
    const updatedServices = additionalServices.map((service) => {
      if (service.id === id) {
        return {
          ...service,
          description: value,
        };
      }

      return service;
    });

    setAdditionalServices(updatedServices);
  };

  const handleDeleteService = (serviceId: string) => {
    const updatedServices = additionalServices.filter(
      (service) => service.id !== serviceId
    );
    setAdditionalServices(updatedServices);
  };

  useEffect(() => {
    if (otherAmount) {
      setOtherAmountVat(getVatCost(otherAmount));
    } else {
      setOtherAmountVat(0);
    }
  }, [otherAmount]);

  useEffect(() => {
    const provisionOfPalletPrice = warehouse?.provisionOfPalletPrice || 0;
    const cost = palletsProvisionedQuantity * provisionOfPalletPrice;
    setPalletsProvisionedCost(cost);
    setPalletsProvisionedVat(getVatCost(cost));
  }, [palletsProvisionedQuantity]);

  useEffect(() => {
    const surchargeFlatFee = warehouse?.additionalSurchargesFlatFee || 0;
    const cost = surchargesAppliedQuantity * surchargeFlatFee;
    setSurchargesAppliedCost(cost);
    setSurchargesAppliedVat(getVatCost(cost));
  }, [surchargesAppliedQuantity]);

  useEffect(() => {
    fetchWarehouseMatch();
    fetchWarehouse();
  }, []);

  const pages = [
    { name: 'Home', href: '/', current: false },
    { name: 'Invoices', href: '/invoices', current: false },
    {
      name: invoice.invoiceNumber || 'Proforma',
      href: `/invoice/${billingCycle.id}`,
      current: true,
    },
  ];

  return (
    <>
      <PageHeader
        header={
          hasConfirmed ? `Invoice ${invoice.invoiceNumber}` : 'Proforma Invoice'
        }
        pages={pages}
      />
      <main className='flex flex-col gap-6 p-4 pb-16 lg:px-8 lg:pb-24'>
          <InvoiceSummary
            isLsp
            hasConfirmed={hasConfirmed}
            hasPaid={hasPaid}
            match={match}
            billingCycle={billingCycle}
            invoice={invoice}
          />
          <InvoiceTable
            billingCycle={billingCycle}
            invoice={invoice}
            invoiceLines={invoiceLines}
            match={match}
            subTotalAmount={subTotalAmount}
            subTotalVatAmount={subTotalVatAmount}
            editSectionProps={
              hasConfirmed
                ? null
                : {
                    warehouse,
                    handleAdditionalServiceDescriptionChange,
                    additionalServices,
                    setAdditionalServices,
                    setPalletsProvisionedQuantity,
                    palletsProvisionedCost,
                    palletsProvisionedVat,
                    setSurchargesAppliedQuantity,
                    surchargesAppliedCost,
                    surchargesAppliedVat,
                    setOtherAmount,
                    otherAmountVat,
                    handleDeleteService,
                    manHours,
                    setManHours,
                    auxiliaryServicesPrice,
                    setAuxiliaryServicesPrice,
                  }
            }
          />
          <InvoicePaymentDetails
            isLsp
            hasConfirmed={hasConfirmed}
            billingCycle={billingCycle}
            invoice={invoice}
            invoiceNotes={invoiceNotes}
            dueDate={dueDate}
            isConfirming={isConfirming}
            handleConfirmInvoice={handleConfirmInvoice}
            setHasPaid={setHasPaid}
          />
      </main>
    </>
  );
}
